import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Zap, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';

export const MarketingLayout = ({ children }) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white/90 backdrop-blur-sm border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto h-full px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Zap className="w-7 h-7 text-orange-500" />
            <span className="font-black text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors ${location.pathname === '/' ? 'text-orange-600' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Home
            </Link>
            <Link 
              to="/pricing" 
              className={`text-sm font-medium transition-colors ${location.pathname === '/pricing' ? 'text-orange-600' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Pricing
            </Link>
            <Link to="/auth">
              <Button variant="ghost" className="text-slate-600">Sign In</Button>
            </Link>
            <Link to="/check">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold" data-testid="get-started-btn">
                Analyse Your Contract
              </Button>
            </Link>
          </nav>

          {/* Mobile menu button */}
          <Button 
            variant="ghost" 
            className="md:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile nav */}
        {menuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-slate-200 p-4 space-y-4">
            <Link 
              to="/" 
              className="block py-2 text-slate-600 hover:text-slate-900"
              onClick={() => setMenuOpen(false)}
            >
              Home
            </Link>
            <Link 
              to="/pricing" 
              className="block py-2 text-slate-600 hover:text-slate-900"
              onClick={() => setMenuOpen(false)}
            >
              Pricing
            </Link>
            <Link 
              to="/auth" 
              className="block py-2 text-slate-600 hover:text-slate-900"
              onClick={() => setMenuOpen(false)}
            >
              Sign In
            </Link>
            <Link to="/check" onClick={() => setMenuOpen(false)}>
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                Analyse Your Contract
              </Button>
            </Link>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="pt-16">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-6 h-6 text-orange-500" />
                <span className="font-black text-lg text-white" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
              </div>
              <p className="text-sm">Pre-submission commercial decision check for UK subcontractors</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-sm text-center space-y-2">
            <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <span className="text-slate-700">|</span>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <span className="text-slate-700">|</span>
              <Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link>
            </div>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              Bidsentry Ltd &bull; Registered in England and Wales &bull; Company No: 17096278 &bull; Registered Office: 124 City Road, London, EC1V 2NX, United Kingdom
            </p>
            <p className="text-slate-600">
              &copy; {new Date().getFullYear()} BidSentry. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
